<template>
  <div class="activity-company-container">
    <Card>
      <p slot="title">企业管理 - {{ activityInfo.title }}</p>

      <!-- 搜索区域 -->
      <div class="search-con">
        <Button type="default" @click="handleBack">返回列表</Button>
        <Input
          v-model="searchParams.company_name"
          placeholder="请输入企业名称"
          style="width: 300px; margin-right: 10px"
          clearable
          @on-enter="handleSearch"
        >
          <span slot="prepend">企业名称</span>
        </Input>

        <Input
          v-model="searchParams.contacts"
          placeholder="请输入联系人"
          style="width: 200px; margin-right: 10px"
          clearable
          @on-enter="handleSearch"
        >
          <span slot="prepend">联系人</span>
        </Input>

        <Input
          v-model="searchParams.phone"
          placeholder="请输入联系电话"
          style="width: 200px; margin-right: 10px"
          clearable
          @on-enter="handleSearch"
        >
          <span slot="prepend">联系电话</span>
        </Input>

        <Button type="primary" @click="handleSearch">搜索</Button>
        <Button type="success" @click="handleAdd" style="margin-left: 10px">添加企业</Button>
        <Button type="warning" @click="handleImportJobs" style="margin-left: 10px">导入职位</Button>
      </div>

      <!-- 企业列表 -->
      <div style="margin-top: 20px">
        <Table
          :columns="columns"
          :data="tableData"
          :loading="loading"
          border
          stripe
          @on-sort-change="handleSortChange"
        >
          <template slot-scope="{ row }" slot="action">
            <Button type="primary" size="small" @click="handleEdit(row)">编辑</Button>
            <Button
              type="warning"
              size="small"
              style="margin-left: 10px"
              @click="handleViewJobs(row)"
            >查看职位</Button>
            <Button
              type="error"
              size="small"
              style="margin-left: 10px"
              @click="handleDelete(row)"
            >删除</Button>
          </template>
        </Table>

        <!-- 分页 -->
        <div style="margin-top: 20px; text-align: right">
          <Page
            :total="total"
            :current="pageParams.page"
            :page-size="pageParams.limit"
            @on-change="handlePageChange"
            @on-page-size-change="handlePageSizeChange"
            show-sizer
            show-total
          />
        </div>
      </div>
    </Card>

    <!-- 添加企业弹窗 -->
    <Modal
      v-model="modalVisible"
      :title="modalTitle"
      width="600px"
      @on-ok="handleSubmit"
      @on-cancel="modalVisible = false"
    >
      <Form ref="companyForm" :model="formData" :rules="formRules">
        <FormItem label="企业名称" prop="company_id">
          <Select
            v-model="formData.company_id"
            placeholder="请选择企业"
            filterable
            clearable
            @on-change="handleCompanyChange"
          >
            <Option
              v-for="item in companySelectList"
              :key="item.id"
              :value="item.id"
            >
              {{ item.company_name }}
            </Option>
          </Select>
        </FormItem>

        <FormItem label="联系人" prop="contacts">
          <Input v-model="formData.contacts" placeholder="请输入联系人" />
        </FormItem>

        <FormItem label="联系电话" prop="phone">
          <Input v-model="formData.phone" placeholder="请输入联系电话" />
        </FormItem>

        <FormItem label="备注" prop="remark">
          <Input
            v-model="formData.remark"
            type="textarea"
            :rows="3"
            placeholder="请输入备注"
          />
        </FormItem>
      </Form>

      <div slot="footer">
        <Button @click="modalVisible = false">取消</Button>
        <Button type="primary" @click="handleSubmit">确定</Button>
      </div>
    </Modal>

    <!-- 职位管理弹窗 -->
    <Modal
      v-model="jobModalVisible"
      title="职位管理"
      width="900px"
      :styles="{ top: '20px' }"
    >
      <ActivityJobManagement
        :activityId="$route.params.id"
        :companyId="currentCompanyId"
        @on-update="handleJobUpdate"
      />
    </Modal>

    <!-- 导入进度窗口 -->
    <Modal
      v-model="importProgressVisible"
      title="职位导入进度"
      width="500px"
      :closable="false"
      :mask-closable="false"
      :footer-hide="true"
    >
      <div class="import-progress-container">
        <div class="progress-content">
          <Progress
            :percent="importPercent"
            :stroke-width="10"
            :status="importStatus"
          />
          <div class="progress-text">{{ importProgressText }}</div>
          <div class="progress-status">{{ importStatusText }}</div>
          <div class="progress-tip" v-if="importPercent < 100">导入中请勿关闭窗口...</div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import {
  getActivityDetail,
  getActivityCompanyList,
  addActivityCompany,
  updateActivityCompany,
  deleteActivityCompany,
  getCompanySelectList
} from '@/api/activity.js'
import ActivityJobManagement from './activity-job.vue'
import * as XLSX from 'xlsx'

export default {
  name: 'ActivityCompany',
  components: {
    ActivityJobManagement
  },
  data () {
    return {
      loading: false,
      activityInfo: {},
      tableData: [],
      total: 0,

      searchParams: {
        company_name: '',
        contacts: '',
        phone: ''
      },

      pageParams: {
        page: 1,
        limit: 10
      },

      modalVisible: false,
      modalTitle: '',
      formData: {
        id: null,
        activity_id: null,
        company_id: null,
        contacts: '',
        phone: '',
        remark: ''
      },

      jobModalVisible: false,
      currentCompanyId: null,

      // 导入进度相关
      importProgressVisible: false,
      importPercent: 0,
      importStatus: 'normal',
      importProgressText: '',
      importStatusText: '',

      companySelectList: [],

      formRules: {
        company_id: [
          { required: true, message: '请选择企业', trigger: 'change' }
        ],
        contacts: [
          { required: true, message: '请输入联系人', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: '请输入联系电话', trigger: 'blur' },
          { pattern: /^1[3456789]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ]
      },

      columns: [
        { title: 'ID', key: 'id', width: 80, sortable: true },
        { title: '企业名称', key: 'companyname', width: 250, sortable: true, tooltip: true },
        { title: '企业地址', key: 'companyaddress', width: 200, tooltip: true },
        { title: '联系人', key: 'linkman', width: 150 },
        { title: '联系电话', key: 'phone', width: 150 },
        { title: '职位数量', key: 'job_count', sortable: true },

        { title: '操作', slot: 'action', width: 250, fixed: 'right' }
      ]
    }
  },

  mounted () {
    const activityId = this.$route.params.id
    this.formData.activity_id = activityId
    this.getActivityInfo()
    this.getActivityCompanyList()
    this.getCompanySelectList()
  },

  methods: {
    // 返回列表
    handleBack () {
      this.$router.push('/activity/list')
    },

    // 导入职位
    handleImportJobs () {
      // 创建文件上传 input
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.xls,.xlsx'
      input.onchange = async (e) => {
        const file = e.target.files[0]
        if (!file) return

        this.$Modal.confirm({
          title: '职位导入',
          content: `确定要导入 "${file.name}" 吗？`,
          onOk: async () => {
            await this.importExcel(file)
          }
        })
      }
      input.click()
    },

    // 导入 Excel 文件
    async importExcel (file) {
      try {
        this.loading = true
        this.$Message.info('正在解析 Excel 文件...')

        // 使用 xlsx 库读取 Excel 文件
        const reader = new FileReader()
        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result)
            const workbook = XLSX.read(data, { type: 'array' })
            const firstSheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[firstSheetName]
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

            // 发送数据到后端
            await this.sendImportData(jsonData, file.name)
          } catch (error) {
            console.error('解析 Excel 文件失败:', error)
            this.$Message.error('解析 Excel 文件失败')
          } finally {
            this.loading = false
          }
        }

        reader.onerror = () => {
          this.loading = false
          this.$Message.error('读取文件失败')
        }

        reader.readAsArrayBuffer(file)
      } catch (error) {
        this.loading = false
        console.error('导入 Excel 文件失败:', error)
        this.$Message.error('导入 Excel 文件失败')
      }
    },

    // 发送导入数据到后端
    async sendImportData (dataList, filename) {
      try {
        // 初始化进度窗口
        this.importProgressVisible = true
        this.importPercent = 0
        this.importStatus = 'normal'
        this.importProgressText = '正在建立连接...'
        this.importStatusText = '准备中'

        // 建立 WebSocket 连接
        const activityId = this.$route.params.id
        const wsUrl = `ws://${window.location.host}/v1/activity/job/import`
        const ws = new WebSocket(wsUrl)

        ws.onopen = () => {
          this.importProgressText = '连接成功，准备导入数据...'
          this.importStatusText = '建立连接'
          // 发送数据
          ws.send(JSON.stringify({
            data_list: dataList,
            filename: filename,
            activity_id: activityId
          }))
        }

        ws.onmessage = (event) => {
          let message
          try {
            message = JSON.parse(event.data)
          } catch (e) {
            // 如果不是 JSON 格式，忽略
            return
          }

          if (message.code === 1) {
            // 处理进度信息
            const percent = Math.round(message.percent)
            const current = message.index
            const total = message.max
            this.importPercent = percent
            this.importProgressText = `正在导入：${current}/${total}`
            this.importStatusText = '导入中'
          } else if (message.code === 0) {
            // 导入完成
            this.importPercent = 100
            this.importStatus = 'success'
            this.importProgressText = message.msg
            this.importStatusText = '导入完成'

            // 延迟关闭窗口
            setTimeout(() => {
              this.importProgressVisible = false
              this.$Message.success('导入成功')
              this.getActivityCompanyList()
            }, 2000)
            ws.close()
          } else if (message.code === -1) {
            // 导入失败
            this.importStatus = 'error'
            this.importProgressText = message.msg || '导入失败'
            this.importStatusText = '导入失败'

            setTimeout(() => {
              this.importProgressVisible = false
              this.$Message.error(message.msg || '导入失败')
            }, 2000)
            ws.close()
          }
        }

        ws.onerror = (error) => {
          this.importStatus = 'error'
          this.importProgressText = 'WebSocket 连接错误'
          this.importStatusText = '连接失败'

          setTimeout(() => {
            this.importProgressVisible = false
            this.$Message.error('WebSocket 连接错误')
          }, 2000)
          console.error('WebSocket 错误:', error)
        }

        ws.onclose = () => {
          this.loading = false
        }
      } catch (error) {
        this.importProgressVisible = false
        this.loading = false
        console.error('发送导入数据失败:', error)
        this.$Message.error('发送导入数据失败')
      }
    },

    // 获取活动信息
    async getActivityInfo () {
      try {
        const res = await getActivityDetail(this.$route.params.id)
        // console.log(res)
        if (res.data.code === 0) {
          this.activityInfo = res.data.data
        } else {
          this.$Message.error(res.data.msg || '获取活动信息失败')
        }
      } catch (error) {
        this.$Message.error('获取活动信息失败')
        console.error(error)
      }
    },

    // 获取活动企业列表
    async getActivityCompanyList () {
      this.loading = true
      try {
        const params = {
          ...this.searchParams,
          page: this.pageParams.page,
          limit: this.pageParams.limit
        }
        const res = await getActivityCompanyList(this.$route.params.id, params)
        // console.log(res)
        if (res.data.code === 0) {
          this.tableData = res.data.data.list || []
          this.total = res.data.data.total || 0
        } else {
          this.$Message.error(res.data.msg || '获取企业列表失败')
        }
      } catch (error) {
        this.$Message.error('获取企业列表失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    // 获取企业选择列表
    async getCompanySelectList () {
      try {
        const res = await getCompanySelectList({})
        if (res.data.code === 0) {
          this.companySelectList = res.data.data || []
        } else {
          this.$Message.error(res.data.msg || '获取企业列表失败')
        }
      } catch (error) {
        this.$Message.error('获取企业列表失败')
        console.error(error)
      }
    },

    // 搜索
    handleSearch () {
      this.pageParams.page = 1
      this.getActivityCompanyList()
    },

    // 排序
    handleSortChange ({ column, key, order }) {
      if (order === 'asc') {
        this.tableData.sort((a, b) => a[key] > b[key] ? 1 : -1)
      } else if (order === 'desc') {
        this.tableData.sort((a, b) => a[key] < b[key] ? 1 : -1)
      }
    },

    // 分页
    handlePageChange (page) {
      this.pageParams.page = page
      this.getActivityCompanyList()
    },

    handlePageSizeChange (pageSize) {
      this.pageParams.limit = pageSize
      this.pageParams.page = 1
      this.getActivityCompanyList()
    },

    // 添加企业
    handleAdd () {
      this.modalTitle = '添加企业'
      this.formData = {
        id: null,
        activity_id: this.$route.params.id,
        company_id: null,
        contacts: '',
        phone: '',
        remark: ''
      }
      this.modalVisible = true
    },

    // 编辑企业
    handleEdit (row) {
      this.modalTitle = '编辑企业'
      this.formData = { ...row }
      this.modalVisible = true
    },

    // 删除企业
    handleDelete (row) {
      this.$Modal.confirm({
        title: '确认删除',
        content: '确定要删除该企业吗？删除后该企业的职位信息也将被删除。',
        onOk: async () => {
          try {
            const res = await deleteActivityCompany(row.id)
            if (res.data.code === 0) {
              this.$Message.success('删除成功')
              this.getActivityCompanyList()
            } else {
              this.$Message.error(res.data.msg || '删除失败')
            }
          } catch (error) {
            this.$Message.error('删除失败')
            console.error(error)
          }
        }
      })
    },

    // 查看职位
    handleViewJobs (row) {
      console.log('查看职位按钮点击，行数据:', row)

      // 检查是否有comid字段
      if (row.comid) {
        this.currentCompanyId = row.comid
        console.log('设置currentCompanyId:', this.currentCompanyId)
        console.log('路由参数:', this.$route.params.id)
        this.jobModalVisible = true
        console.log('jobModalVisible设置为true')
      } else {
        console.error('没有找到comid字段:', row)
        this.$Message.error('企业ID信息缺失')
      }
    },

    // 企业选择变化
    handleCompanyChange (companyId) {
      const company = this.companySelectList.find(item => item.id === companyId)
      if (company) {
        // 可以自动填充企业的联系人信息
      }
    },

    // 提交表单
    async handleSubmit () {
      try {
        this.$refs.companyForm.validate(async (valid) => {
          if (valid) {
            let res
            if (this.formData.id) {
              res = await updateActivityCompany(this.formData)
            } else {
              res = await addActivityCompany(this.formData)
            }

            if (res.data.code === 0) {
              this.$Message.success(this.formData.id ? '更新成功' : '添加成功')
              this.modalVisible = false
              this.getActivityCompanyList()
            } else {
              this.$Message.error(res.data.msg || '操作失败')
            }
          }
        })
      } catch (error) {
        this.$Message.error('操作失败')
        console.error(error)
      }
    },

    // 职位更新回调
    handleJobUpdate () {
      this.jobModalVisible = false
      // 可以在这里刷新数据
    }
  }
}
</script>

<style scoped>
.activity-company-container {
  padding: 10px;
}

.search-con {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.import-progress-container {
  padding: 20px 0;
  text-align: center;
}

.progress-content {
  .progress-text {
    margin-top: 20px;
    font-size: 16px;
    font-weight: 500;
    color: #2d8cf0;
  }

  .progress-status {
    margin-top: 10px;
    font-size: 14px;
    color: #666;
  }

  .progress-tip {
    margin-top: 20px;
    font-size: 12px;
    color: #999;
  }
}
</style>
